/**
 * Quarterly plan-upsell step — the first screen of the post-purchase
 * drawer for `quarterly-renewal-upsell-v1` variant-a (monthly gluta/NAD/
 * serm purchases only; eligibility resolved server-side on
 * /me/post-purchase-offer → plan_upsell).
 *
 * The pitch: keep the month you just bought exactly as-is, but have the
 * plan RENEW quarterly — standard quarterly renewal price (the intro
 * discount was already spent on the monthly purchase), shown as a
 * per-month equivalent next to the monthly renewal so the savings are
 * legible. The "N vials up front" line only renders when the quarterly
 * shipment pattern actually front-loads >1 container (BUD/max-supply
 * permitting) — the backend nulls it otherwise.
 *
 * Visual language matches the rest of the drawer (mono uppercase, the
 * #f5f5f5 title block, dark summary panel).
 */

import type { PlanUpsell } from '@/lib/api/upsellHooks';

function fmtDollars(cents: number): string {
  const v = cents / 100;
  return v % 1 === 0 ? `$${v}` : `$${v.toFixed(2)}`;
}

function fmtRenewalDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
}

interface Props {
  plan: PlanUpsell;
  state: 'offer' | 'accepted';
  accepting: boolean;
  attempted?: boolean;
  error: string | null;
  onAccept: () => void;
  onDecline: () => void;
  onContinue: () => void;
  /** Whether treatment upsell cards follow this step — changes the
   *  accepted-state button copy ("Continue" vs "Done"). */
  hasTreatmentsNext: boolean;
}

export function QuarterlyPlanStep({
  plan,
  state,
  accepting,
  attempted = false,
  error,
  onAccept,
  onDecline,
  onContinue,
  hasTreatmentsNext,
}: Props) {
  const renewalDate = fmtRenewalDate(plan.next_renewal_date);
  const savingsMo = fmtDollars(plan.savings_per_month_cents);
  const quarterlyTotal = fmtDollars(plan.quarterly_renewal_cents);
  const quarterlyPerMo = fmtDollars(plan.quarterly_per_month_cents);
  const monthlyMo = fmtDollars(plan.monthly_renewal_cents);
  const isStack = (plan.items?.length ?? 0) > 1;
  const showVials =
    plan.first_shipment_containers != null &&
    plan.first_shipment_containers > 1;

  if (state === 'accepted') {
    return (
      <div className="flex flex-col w-full">
        <div className="bg-[#f5f5f5] flex flex-col gap-8 px-15 pt-15 pb-15 w-full">
          <p className="font-mono text-22 md:text-24 text-black uppercase tracking-[-0.48px] m-0 leading-110">
            Locked in —
            <br />
            quarterly savings on
          </p>
          <p className="leading-130 text-14 md:text-16 text-[#3a3a3a] m-0">
            Nothing changes this month. Starting
            {renewalDate ? ` ${renewalDate}` : ' at each next renewal'}, your
            {isStack ? ` ${plan.items!.length} stack subscriptions renew` : ' plan renews'} quarterly at {quarterlyTotal} {isStack ? 'combined ' : ''}every 3 months (
            {quarterlyPerMo}/mo) — saving you {savingsMo} every month
            {showVials
              ? `, with ${plan.first_shipment_containers} ${
                  plan.container_noun ?? 'units'
                } shipping up front`
              : ''}
            .
            {isStack && ' Each product remains a separate subscription.'}
          </p>
        </div>
        <div className="px-15 py-15">
          <button
            type="button"
            onClick={onContinue}
            className="w-full bg-black text-white font-mono text-14 uppercase tracking-[-0.28px] py-14 hover:bg-[#2a2a2a]"
          >
            {hasTreatmentsNext ? 'Continue' : 'Done'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full">
      {/* Title block — same chrome as the treatments step */}
      <div className="bg-[#f5f5f5] flex flex-col gap-8 px-15 pt-15 pb-12 w-full">
        <p className="font-mono text-22 md:text-24 text-black uppercase tracking-[-0.48px] m-0 leading-110">
          Save {savingsMo}/mo on
          <br />
          your {isStack ? 'stack' : plan.product_title} renewal
        </p>
        <p className="leading-130 text-14 md:text-16 text-[#3a3a3a] m-0">
          Your first month&rsquo;s already paid.{' '}
          {renewalDate ? `From your renewal on ${renewalDate}` : isStack ? "From each product’s next renewal" : 'From your next renewal'}
          , switch to quarterly and pay {quarterlyPerMo}/mo instead of{' '}
          {monthlyMo}/mo — {isStack ? 'across all products in this purchase' : 'same treatment'}, billed {quarterlyTotal} {isStack ? 'combined ' : ''}every 3
          months.{isStack && ' Nothing is charged today.'}
        </p>
      </div>

      {isStack && (
        <div className="px-15 py-12 border-b border-line-subtle flex flex-col gap-8">
          <p className="font-mono text-11 text-ink-muted uppercase m-0">All {plan.items!.length} products included</p>
          {plan.items!.map((item) => (
            <div key={item.subscription_id} className="flex justify-between gap-10 text-12 text-ink">
              <span>{item.product_title}<span className="block text-11 text-ink-muted">Starts {fmtRenewalDate(item.next_renewal_date) ?? 'next renewal'}</span></span>
              <span className="font-mono text-right">{fmtDollars(item.quarterly_renewal_cents)} / 3 months</span>
            </div>
          ))}
        </div>
      )}

      {/* Comparison rows — headed by the renewal date so both prices read
          as NEXT-RENEWAL prices, not what the patient just paid (their
          first month was the intro price). */}
      <div className="flex flex-col w-full border-b border-[#ececec]">
        <div className="bg-[#f0f0f0] border-b border-[#ececec] px-15 py-8 font-mono text-11 uppercase tracking-[-0.24px] text-[#5e5e5e]">
          {renewalDate
            ? `Your renewal on ${renewalDate}:`
            : 'At your next renewal:'}
        </div>
        <div className="flex items-center justify-between px-15 py-14 border-b border-[#ececec]">
          <div className="flex flex-col gap-3">
            <span className="font-mono text-13 text-black uppercase leading-tight">
              Monthly
            </span>
            <span className="font-mono text-11 text-[#8a8a8a] uppercase leading-tight">
              Current {isStack ? 'plans — combined' : 'plan — renews at'}
            </span>
          </div>
          <span className="font-mono text-14 text-black">{monthlyMo}/mo</span>
        </div>
        <div className="flex items-center justify-between px-15 py-14 bg-[#fafafa]">
          <div className="flex flex-col gap-3">
            <span className="font-mono text-13 text-black uppercase leading-tight">
              Quarterly
            </span>
            <span className="font-mono text-11 text-[#8a8a8a] uppercase leading-tight">
              {quarterlyTotal} every 3 months
            </span>
          </div>
          <div className="flex flex-col items-end gap-3">
            <span className="font-mono text-14 text-black">
              {quarterlyPerMo}/mo
            </span>
            <span className="font-mono text-11 text-[#0a7a3d] uppercase leading-tight">
              Save {savingsMo}/mo
            </span>
          </div>
        </div>
      </div>

      {showVials && (
        <div className="flex items-start gap-8 px-15 py-12 border-b border-[#ececec]">
          <span className="font-mono text-13 text-black leading-tight">+</span>
          <p className="font-mono text-12 text-[#3a3a3a] uppercase leading-130 m-0">
            {plan.first_shipment_containers} {plan.container_noun ?? 'units'}{' '}
            ship up front on your next order
          </p>
        </div>
      )}

      {/* CTAs */}
      <div className="flex flex-col gap-10 px-15 py-15">
        <button
          type="button"
          onClick={onAccept}
          disabled={accepting}
          className="w-full bg-black text-white font-mono text-14 uppercase tracking-[-0.28px] py-14 hover:bg-[#2a2a2a] disabled:opacity-60"
        >
          {accepting
            ? 'Switching…'
            : attempted ? 'Retry unconfirmed plans' : `Renew ${isStack ? 'all ' : ''}quarterly — save ${savingsMo}/mo`}
        </button>
        <button
          type="button"
          onClick={onDecline}
          disabled={accepting}
          className="w-full font-mono text-12 text-[#5e5e5e] uppercase underline py-6"
        >
          {attempted ? 'Continue — review my plans in Account' : 'No thanks, keep monthly'}
        </button>
        {error && (
          <p role="alert" className="font-mono text-12 text-[#b3261e] leading-130 m-0">
            {error}
          </p>
        )}
      </div>

      {/* Fine print — the load-bearing disclosures, deliberately quiet:
          standard (non-intro) quarterly price, applies at renewal, and
          cancellable before it ever bills. */}
      <p className="px-15 pb-15 text-11 text-[#8a8a8a] leading-140 m-0">
        Applies at {isStack ? 'each product’s' : 'your'} next renewal{renewalDate ? ` on ${renewalDate}` : ''} —
        your current month doesn&rsquo;t change, and you&rsquo;ll be billed{' '}
        {quarterlyTotal} {isStack ? 'combined ' : ''}every 3 months at the standard quarterly rate.{isStack && ' Products are billed separately on their own renewal dates.'} You can
        cancel or switch plans anytime before renewal in Account →
        Subscriptions.
      </p>
    </div>
  );
}
